<script>
  // eslint-disable-next-line  @ObamaFoundation/of-svelte/no-stores
  import { writable, derived } from "svelte/store";
  import Seo from "$lib/components/SEO.svelte";
  import Hero from "$lib/components/Hero/Hero.svelte";
  import Select from "$lib/components/Select.svelte";
  import Heading from "$lib/components/Heading.svelte";
  import { textToId } from "$lib/utilities/dom-helpers.js";
  import Icon from "$lib/components/Icon.svelte";

  export let data;
  const { departmentData, officeData, currentUrl } = data;

  const DEFAULT_DEPT = {
    label: "All Departments",
    value: "All Departments"
  };
  const DEFAULT_OFFICE = {
    label: "All Offices",
    value: "All Offices"
  };
  const NO_DEPT = { id: 4000000000, name: "No Department" };

  function sortDepartments(deptA, deptB) {
    // always put the "No Department" items last
    return deptA.name === NO_DEPT.name ? 99 : deptA.name.localeCompare(deptB.name);
  }

  const departments = writable(departmentData.sort(sortDepartments));
  const offices = writable(officeData);
  const filter = writable({ departmentId: null, officeId: null });

  function onSelectChange(e) {
    filter.update((current) => ({ ...current, [e.detail.name]: parseInt(e.detail.option.value) }));
  }

  const jobsByDepartment = derived([departments, filter], ([$departments, $filter]) => {
    const filterByDeptId = (d) => ($filter.departmentId ? d.id === $filter.departmentId : d.id);
    const filterByHasJobs = (dept) => dept.jobs.length > 0;
    const filterJobsByOfficeId = (job) => job.offices.some((o) => o.id === $filter.officeId);
    const filterByOffice = (department) => {
      const jobs = department.jobs.filter(filterJobsByOfficeId);
      return {
        ...department,
        jobs: $filter.officeId ? jobs : department.jobs
      };
    };

    return $departments.filter(filterByDeptId).map(filterByOffice).filter(filterByHasJobs);
  });

  const PAGE_DATA = {
    fields: {
      headline: "View Open Roles",
      parentInfo: {
        headline: "Careers",
        relativeUrl: "/careers/"
      }
    }
  };

  const resultsHeadingText = "Jobs";
  $: selectedDepartmentName = $departments.find((dept) => dept.id === $filter.departmentId)?.name;
  $: selectedOfficeName = $offices.find((office) => office.id === $filter.officeId)?.name;
  $: liveRegionText = `${$jobsByDepartment.length > 0 ? "Viewing jobs" : "No jobs found"} for ${
    selectedDepartmentName ? `the ${selectedDepartmentName} department` : "all departments"
  } at ${selectedOfficeName ? `our ${selectedOfficeName} office` : "all offices"}.`;
</script>

<Seo data={PAGE_DATA.fields} {currentUrl} cookieBanner={data.cookieBanner} />

<div class="w-full px-24 md:px-175 lg:max-w-1014 lg:px-0 m-auto" id="page-header">
  <Hero title={PAGE_DATA.fields.headline} parentInfo={PAGE_DATA.fields.parentInfo} />

  <div class="w-auto page-level-padding internal-page-spacing">
    <p class="">
      We have ambitious plans, and we know the most critical step is building an incredible team
      that reflects the diverse fabric of our nation and embodies our values. Our team is made up of
      people who are equal parts dreamers and doers. We are people who care as much about how we
      achieve our goals as we do about the goals themselves. Our values drive our team and are
      instilled in the work this Foundation does every day. We are also committed to creating an
      anti-racist organization in order to do our part to help combat racism and inequity in all
      forms, in communities across our nation and around the world.

      <a
        href="/discover/stories/anti-racism-equity-statement/"
        class="text-primary-700 underline"
        data-sveltekit-reload
      >
        Read our full statement on anti-racism and equity here.
      </a>
    </p>

    <form on:submit={(evt) => evt.preventDefault()}>
      <div class="flex flex-wrap gap-x-[144px] gap-y-40">
        <Select
          name="departmentId"
          label="Select a Department"
          options={[DEFAULT_DEPT].concat(
            $departments.map((dept) => ({
              label: dept.name,
              value: dept.id
            }))
          )}
          on:select={onSelectChange}
        />

        <Select
          name="officeId"
          label="Select a Location"
          options={[DEFAULT_OFFICE].concat(
            $offices.map((office) => ({
              label: office.name,
              value: office.id
            }))
          )}
          on:select={onSelectChange}
        />
      </div>
    </form>

    <div class="last-page-element">
      <Heading
        class="sr-only font-heading text-size-24 md:text-size-32"
        text={resultsHeadingText}
      />

      <div class="sr-only" aria-live="polite" aria-atomic="true">{liveRegionText}</div>

      {#if !$jobsByDepartment.length}
        <p class="font-heading text-size-24 md:text-size-32 text-center min-h-[30vh]">
          No Listings Found
        </p>
      {:else}
        <ul
          class="list-none flex flex-col gap-y-48 md:gap-y-32"
          aria-labelledby={textToId(resultsHeadingText)}
        >
          {#each $jobsByDepartment as department}
            <li>
              <Heading class="font-heading text-size-24 md:text-size-32" text={department.name} />

              <hr class="text-neutral-300 mt-16" aria-hidden="true" />

              <ul class="list-none" aria-labelledby={textToId(department.name)}>
                {#each department.jobs as job}
                  <li
                    class="flex flex-col md:flex-row justify-between items-start md:items-center gap-32 py-32 md:py-48"
                  >
                    <div class="flex flex-col gap-y-24 md:max-w-[511px]">
                      <p class="font-bold text-size-20">{job.title}</p>
                      <p class="text-gray-700">{job.location.name}</p>
                    </div>

                    <div class="flex-shrink-0">
                      <a
                        href={job.absolute_url}
                        class="btn btn-tertiary focus:rounded-35"
                        target="_blank"
                        rel="noopener noreferrer"
                        >View Job
                        <Icon type="external" size="md" class="!inline ml-8 align-top" />
                      </a>
                    </div>
                  </li>
                  <hr class="text-neutral-100 last-of-type:hidden" aria-hidden="true" />
                {/each}
              </ul>
            </li>
          {/each}
        </ul>
      {/if}
    </div>
  </div>
</div>
